import React from 'react';
import ThreatItem from './ThreatItem';
import { Text } from '@fluentui/react-components';
import { useResultStore } from '../../../providers/ResultProvider';
import { ThreatResultType, ThreatType } from '../../../components/threat/threat.config';
import { useQuery } from '@tanstack/react-query';
import { getScanResultByGroupId } from '../../../api';
import { IndicatorStatusType, ScanResultByGroupIdType } from '../../../types';
import { ProgressBarProvider } from '../../../components/ProgressBarProvider';
import { useTabStore } from '../../../providers/TabProvider';


interface ThreatListProps {
    threatResults?: ScanResultByGroupIdType;
}

export default function ThreatList({ threatResults }: ThreatListProps) {
    const deepScanData = useResultStore((s) => s.deepScanData);
    const groupId = deepScanData?.group_id;
    const emailData = useTabStore((s) => s.emailData);

    // If props are provided (History View), use them. Otherwise, fetch.
    const isHistoryView = !!threatResults;

    const {
        isPending,
        data: itemData,
    } = useQuery<{
        result: ScanResultByGroupIdType;
        overall_status: ThreatType;
        description: string;
    }>({
        queryKey: ['threat-scan-results', groupId, emailData?.emailFingerprint],
        refetchInterval: 10000,
        enabled: !isHistoryView && !!emailData?.emailFingerprint,
        queryFn: async () => {

            return await getScanResultByGroupId({ groupId, emailData });
        },
    });
    const dataResults = isHistoryView ? (threatResults || {}) : (itemData?.result || {});

    if (isPending && !isHistoryView) {
        return <ProgressBarProvider />;
    }

    if (Object.keys(dataResults).length == 0) return (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <Text as="h1" weight="bold">
                No results found
            </Text>
        </div>
    )

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <Text as="h1" weight="bold">
                Results
            </Text>
            <div>
                {Object.entries(dataResults).map(([threatKey, threatValue]) => {
                    const items = Array.isArray(threatValue)
                        ? threatValue : [];

                    return (
                        <ThreatItem
                            key={threatKey}
                            threatKey={threatKey as ThreatResultType}
                            items={items}
                        />
                    );
                })}
            </div>

            {!isHistoryView && (
                <Provider
                    threatResults={dataResults}
                    threatStatusDetails={{
                        "deep-scan": {
                            status: itemData?.overall_status || "Unrated",
                            description: itemData?.description || "",
                        }
                    }}
                />
            )}
        </div>
    );
}


function Provider({
    threatResults,
    threatStatusDetails,
}: {
    threatResults: Partial<ScanResultByGroupIdType>;
    threatStatusDetails?: IndicatorStatusType
}) {
    const setThreatItems = useResultStore((s) => s.setThreatItems);
    const setIndicatorStatus = useResultStore((s) => s.setIndicatorStatus);

    // Use a useEffect to set the total number of items when the component mounts
    React.useEffect(() => {
        setThreatItems(threatResults);
        setIndicatorStatus(threatStatusDetails);
    }, [threatResults]);

    return null
}